/* eslint-disable max-len */
/** @jsxImportSource @emotion/react */
import { FunctionComponent, useState, useEffect, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { css } from '@emotion/react';
import ReactTooltip from 'react-tooltip';
import Modal from 'react-bootstrap/Modal';
import { hooks as commonHooks, types as commonTypes } from '@sitebuilder/common';
import HeaderWrapper from '../Header/HeaderWrapper';
import { Headings } from '../Header/Header';
import {
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
} from '../utils';
import modalStyles, { themeModalStyles } from './Modal.styles';

const CHILDREN_PLACEHOLDER_ID = 'modal';
interface WidgetProperties extends commonTypes.WidgetPropertiesBase {
    id?: string;
    openButtonHtml: string;
    showModalInEditMode?: boolean;
    modalButtonAriaLabel: string;
    titles?: Headings[];
    closeButtonHtml: string;
    showToolTip?: boolean;
    tooltipOptions: { text: string; textColor: string; backgroundColor: string };
    typographyClass?: string;
    isWithinIframe?: boolean;
    eventTypeName?: string;
    topOffsetPercentage?: string;
    isShowPrintButton?: boolean;
    printButtonHtml?: string;
    printDocumentName?: string;
}

const ModalContainer: FunctionComponent<
    React.PropsWithChildren<commonTypes.WidgetInstanceProps>
> = (props: commonTypes.WidgetInstanceProps) => {
    const widgetInstance = commonHooks.useWidgetInstance(props.widgetInstanceId);
    const widgetProperties =
        commonHooks.useWidgetProperties<WidgetProperties>(props.widgetInstanceId) ||
        props?.properties;
    const { styles, id, openButtonHtml, showModalInEditMode, titles, closeButtonHtml, ...rest } =
        widgetProperties || props;
    const { data } = props;
    const theme = data?.themeSelected || props?.theme;
    const { inEditMode, Placeholder } = commonHooks.useAppContext();
    const i18n = commonHooks.useLocalization();
    const [show, setShow] = useState(false);
    const [position, setPosition] = useState(0);
    const [isMounted, setIsMounted] = useState(false);

    const contentToPrint = useRef(null);
    const openButtonRef = useRef<any>(null);

    const handlePrint = useReactToPrint({
        documentTitle: rest.printButtonHtml
            ? i18n.format(rest.printDocumentName, data)
            : 'Print This Document',
        removeAfterPrint: true,
    });

    useEffect(() => {
        setIsMounted(true);
    }, []);

    useEffect(() => {
        if (show) {
            // get by data-widget-instance-id
            const modalElement = document.querySelector(
                `[data-widget-instance-id="${props.widgetInstanceId}"]`
            ) as HTMLElement;

            const parentElement = modalElement?.parentElement;

            parentElement?.setAttribute(
                'aria-label',
                i18n.format(rest.modalButtonAriaLabel, data) || ''
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    useEffect(() => {
        const handleMessage = (event: any) => {
            const eventTypeName = rest.eventTypeName || 'scrollAndWindowSize';
            let topOffsetPercentage = parseFloat(rest.topOffsetPercentage || '');

            if (isNaN(topOffsetPercentage)) {
                topOffsetPercentage = 0.05;
            }

            // To test in our parent Iframe
            if (event.data && event.data.type === 'PARENT_SCROLL') {
                const { scrollFromTop, windowHeight } = event.data;
                setPosition(Math.max(0, scrollFromTop - windowHeight * topOffsetPercentage));
            } else if (event.data && event.data.type === eventTypeName) {
                const { scrollTop, windowHeight } = event.data;
                setPosition(Math.max(0, scrollTop - windowHeight * topOffsetPercentage));
            }
        };

        // Setup event listener for the message event
        if (rest.isWithinIframe) {
            window.addEventListener('message', handleMessage);
        }

        // Cleanup the event listener when component unmounts
        return () => {
            if (rest.isWithinIframe) {
                window.removeEventListener('message', handleMessage);
            }
        };
    }, [rest.isWithinIframe, rest.eventTypeName, rest.topOffsetPercentage]);

    const handleClose = () => {
        setShow(false);

        // post message to parent Iframe on close
        if (rest.isWithinIframe) {
            window.parent.postMessage('modalClose', '*');
        }

        // Blur the button
        if (openButtonRef) {
            setTimeout(() => openButtonRef.current.blur(), 50);
        }
    };

    const handleShow = () => {
        setShow(true);

        // post message to parent Iframe on open
        if (rest.isWithinIframe) {
            window.parent.postMessage('modalOpen', '*');
        }
    };

    return (
        <div
            className={`modal-widget ${data?.locale === 'fr-CA' ? 'french' : ''} ${
                widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''
            }`}
            id={id}
            css={css`
                ${modalStyles}${theme ? themeModalStyles : ''}${getTextSchemaProperties(
                    widgetProperties,
                    data?.colorSpace
                )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                    widgetProperties
                )}${styles || ''}
            `}
            ref={(el) => {
                if (!el) return;

                if (!rest.isWithinIframe) {
                    setPosition(el.getBoundingClientRect().y);
                }
            }}
        >
            <div>
                <button
                    className="open-modal-button"
                    onClick={handleShow}
                    aria-label={i18n.format(rest.modalButtonAriaLabel, data) || ''}
                    ref={openButtonRef}
                >
                    <p
                        className="open-button"
                        data-tip
                        data-for={`title-tooltip-${props.widgetInstanceId}`}
                        dangerouslySetInnerHTML={{
                            __html: i18n.format(openButtonHtml, data),
                        }}
                    />
                    {rest.showToolTip && isMounted && (
                        <ReactTooltip
                            id={`title-tooltip-${props.widgetInstanceId}`}
                            textColor={rest.tooltipOptions.textColor || '#fff'}
                            backgroundColor={rest.tooltipOptions.backgroundColor}
                        >
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: i18n.format(rest.tooltipOptions?.text, data),
                                }}
                            />
                        </ReactTooltip>
                    )}
                </button>

                <Modal
                    data-testid="modal"
                    data-widget-instance-id={widgetInstance?.id}
                    show={show}
                    onHide={handleClose}
                    size="lg"
                    style={{ top: position }}
                    css={css`
                        ${modalStyles}${theme ? themeModalStyles : ''}${getTextSchemaProperties(
                            widgetProperties
                        )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                            widgetProperties
                        )}${styles || ''}
                    `}
                >
                    <div>
                        <div className="modal-custom-header">
                            <Modal.Title style={{ width: '100%' }} className={'modal-title'}>
                                <HeaderWrapper headings={titles} data={data} />
                            </Modal.Title>
                            <button
                                className="close-modal-button"
                                onClick={handleClose}
                                aria-label={i18n.translate('widgets_closeModal') as string}
                            >
                                <p
                                    className="close-button"
                                    dangerouslySetInnerHTML={{
                                        __html: i18n.format(closeButtonHtml, data),
                                    }}
                                />
                            </button>
                        </div>
                        <div className="modal-body" ref={contentToPrint}>
                            {rest.isShowPrintButton ? (
                                <button
                                    data-testid="print-button"
                                    onClick={() => {
                                        handlePrint(null, () => contentToPrint.current);
                                    }}
                                    dangerouslySetInnerHTML={{
                                        __html: i18n.format(rest.printButtonHtml, data),
                                    }}
                                />
                            ) : (
                                <></>
                            )}
                            <Placeholder
                                contentPlaceHolderId={CHILDREN_PLACEHOLDER_ID}
                                parentWidgetInstanceId={widgetInstance?.id}
                                data={data}
                            />
                        </div>
                    </div>
                </Modal>
                {inEditMode && showModalInEditMode ? (
                    <Placeholder
                        contentPlaceHolderId={CHILDREN_PLACEHOLDER_ID}
                        parentWidgetInstanceId={widgetInstance?.id}
                        data={data}
                    />
                ) : (
                    ''
                )}
            </div>
        </div>
    );
};

export default ModalContainer;
